import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Play, Upload, Loader2, Database, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { MigrationProfileDetail } from '@/components/MigrationProfileDetail';
import { MappingWorkbench } from '@/components/MappingWorkbench';
import { MigrationLoader } from '@/components/MigrationLoader';
import { ClientPacks } from '@/components/ClientPacks';
import { ClassKeyCleanup } from '@/components/ClassKeyCleanup';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const n = (v) => (v || 0).toLocaleString();
const score = (s) => (s >= 95 ? C.green : s >= 75 ? C.amber : C.red);

function CanonicalStrip({ shape }) {
  const [open, setOpen] = useState(false);
  if (!shape) return null;
  return (
    <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="canonical-card">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>The canonical six</h3>
          <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>{shape.note}</p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => setOpen(o => !o)} className="text-[11px] h-6 px-2 rounded-sm"
          style={{ color: C.blue }} data-testid="canonical-toggle">{open ? 'Hide shape' : 'Show shape'}</Button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {shape.entities.map(e => (
          <span key={e.entity} className="px-2 py-1 rounded-sm text-[10px]" data-testid={`canonical-chip-${e.entity}`}
            style={{ background: 'rgba(26,76,124,0.06)', color: C.navy }}>
            <b>{e.label}</b> · {e.fields.length} fields · key {e.key}{e.links.length ? ` · ${e.links.length} link${e.links.length > 1 ? 's' : ''}` : ''}
          </span>
        ))}
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          {shape.entities.map(e => (
            <div key={e.entity}>
              <p className="text-[11px] font-semibold" style={{ color: C.navy }}>{e.label} <span className="font-normal" style={{ color: C.slate }}>— {e.note}</span></p>
              <p className="text-[10px]" style={{ color: C.slate }}>
                {e.fields.map(f => `${f.label}${f.required ? '*' : ''} (${f.rule})`).join(' · ')}
              </p>
            </div>
          ))}
          <p className="text-[10px]" style={{ color: C.slate }}>* required on the canonical shape. Transformation rules travel with every field into the lineage register.</p>
        </div>
      )}
    </Card>
  );
}

function PlatformRunCard({ api, shape, onRan }) {
  const [picked, setPicked] = useState([]);
  const [cap, setCap] = useState(12000);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/migration/profile/platform',
        { entities: picked.length ? picked : null, sample_cap: Number(cap) || 12000 }, { timeout: 300000 });
      toast.success(`Profiled ${n(data.totals.rows)} rows — ${data.totals.issues} findings`);
      onRan(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Profile run failed'); }
    setBusy(false);
  };
  return (
    <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="platform-run-card">
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate }}>Profile this platform's register</h3>
      <p className="text-[11px] mb-2" style={{ color: C.slate }}>
        The target side of a reconciliation. Rehearsal (simulation) rows are counted and excluded — the book is profiled, not the dress rehearsal.
      </p>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(shape?.entities || []).map(e => {
          const on = picked.includes(e.entity);
          return (
            <button key={e.entity} onClick={() => setPicked(p => on ? p.filter(x => x !== e.entity) : [...p, e.entity])}
              data-testid={`entity-toggle-${e.entity}`} className="px-2 py-1 rounded-sm text-[10px] font-medium transition-colors"
              style={{ background: on ? C.navy : 'rgba(136,150,168,0.10)', color: on ? '#fff' : C.slate }}>
              {e.label}
            </button>
          );
        })}
      </div>
      <div className="flex items-end gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: C.slate }}>Sample cap per entity</p>
          <Input type="number" value={cap} onChange={e => setCap(e.target.value)} className="text-xs rounded-sm w-28 h-8"
            data-testid="sample-cap-input" />
        </div>
        <Button size="sm" onClick={run} disabled={busy} className="text-xs rounded-sm h-8" style={{ background: C.navy }}
          data-testid="profile-platform-btn">
          {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Profiling…</> : <><Play className="w-3.5 h-3.5 mr-1" />Run profile{picked.length ? ` (${picked.length})` : ' (all six)'}</>}
        </Button>
      </div>
    </Card>
  );
}

function ExtractCard({ api, shape, onRan }) {
  const [entity, setEntity] = useState('policy');
  const [client, setClient] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    const fd = new FormData();
    fd.append('entity', entity); fd.append('client', client); fd.append('file', file);
    setBusy(true);
    try {
      const { data } = await api.post('/migration/profile/extract', fd, { timeout: 300000 });
      toast.success(`Profiled ${n(data.totals.rows)} extract rows — ${data.totals.issues} findings`);
      setFile(null);
      onRan(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'The extract could not be profiled'); }
    setBusy(false);
  };
  return (
    <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="extract-card">
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate }}>Profile a client extract</h3>
      <p className="text-[11px] mb-2" style={{ color: C.slate }}>
        CSV or spreadsheet. Profiled in memory — <b style={{ color: C.green }}>no client rows are stored</b>, only what the profile measured, and a run deletes in one action.
        This is the same profiler that scopes a migration and the same one behind the Consistency Review.
      </p>
      <div className="grid grid-cols-4 gap-1.5 items-end">
        <div>
          <p className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: C.slate }}>Canonical shape</p>
          <select value={entity} onChange={e => setEntity(e.target.value)} data-testid="extract-entity-select"
            className="w-full text-xs rounded-sm border px-2 h-8" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>
            {(shape?.entities || []).map(e => <option key={e.entity} value={e.entity}>{e.label}</option>)}
          </select>
        </div>
        <Input placeholder="Client / prospect" value={client} onChange={e => setClient(e.target.value)}
          className="text-xs rounded-sm h-8" data-testid="extract-client-input" />
        <input type="file" accept=".csv,.xlsx" onChange={e => setFile(e.target.files?.[0] || null)}
          className="text-xs" data-testid="extract-file-input" />
        <Button size="sm" onClick={run} disabled={busy || !file} className="text-xs rounded-sm h-8"
          style={{ background: C.blue }} data-testid="profile-extract-btn">
          {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Profiling…</> : <><Upload className="w-3.5 h-3.5 mr-1" />Profile extract</>}
        </Button>
      </div>
    </Card>
  );
}

export default function MigrationToolkitPage() {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [shape, setShape] = useState(null);
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('profiling');

  const loadRuns = useCallback(() => api.get('/migration/profile/runs')
    .then(r => setRuns(r.data.runs || [])).catch(() => { }), [api]);

  useEffect(() => {
    Promise.all([api.get('/migration/canonical'), api.get('/migration/profile/runs')])
      .then(([a, b]) => { setShape(a.data); setRuns(b.data.runs || []); })
      .catch(() => toast.error('Failed to load the migration toolkit'))
      .finally(() => setLoading(false));
  }, [api]);

  const openRun = async (run_id) => {
    try {
      const { data } = await api.get(`/migration/profile/runs/${run_id}`);
      setOpen(data);
    } catch { toast.error('Run not found'); }
  };

  if (loading) return <div className="p-8 text-sm" style={{ color: C.slate }}>Loading migration toolkit…</div>;

  return (
    <div className="space-y-4" data-testid="migration-page">
      <p className="text-xs" style={{ color: C.slate }}>
        Phase one of any migration, and the first thing a capacity provider asks for: profile before you scope,
        then agree what every column becomes before a single record moves. Never quote a migration on assumption.
      </p>

      <div className="flex gap-1.5">
        {[['profiling', 'Profiling'], ['mapping', 'Mapping workbench'], ['loader', 'Loader'], ['cleanup', 'Class keys'], ['packs', 'Client pack']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
            className="px-3 py-1.5 rounded-sm text-xs font-medium transition-colors"
            style={{ background: tab === key ? C.navy : 'rgba(136,150,168,0.10)', color: tab === key ? '#fff' : C.slate }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'mapping' ? (
        <MappingWorkbench api={api} isAdmin={isAdmin} runs={runs} />
      ) : tab === 'loader' ? (
        <MigrationLoader api={api} isAdmin={isAdmin} />
      ) : tab === 'cleanup' ? (
        <ClassKeyCleanup api={api} isAdmin={isAdmin} />
      ) : tab === 'packs' ? (
        <ClientPacks api={api} isAdmin={isAdmin} runs={runs} />
      ) : open ? (
        <>
          <Button size="sm" variant="ghost" onClick={() => setOpen(null)} className="text-[11px] h-6 px-2 rounded-sm"
            style={{ color: C.blue }} data-testid="back-to-runs-btn"><ChevronLeft className="w-3 h-3 mr-1" />All profile runs</Button>
          <MigrationProfileDetail run={open} onDeleted={() => { setOpen(null); loadRuns(); }} />
        </>
      ) : (
        <>
          {isAdmin && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <PlatformRunCard api={api} shape={shape} onRan={(r) => { setOpen(r); loadRuns(); }} />
              <ExtractCard api={api} shape={shape} onRan={(r) => { setOpen(r); loadRuns(); }} />
            </div>
          )}
          {!isAdmin && (
            <p className="text-[11px] p-2 rounded-sm" style={{ background: 'rgba(136,150,168,0.08)', color: C.slate }}
              data-testid="migration-read-only-note">
              Profile runs and client extracts are an admin action — past runs are readable here.
            </p>
          )}

          <CanonicalStrip shape={shape} />

          <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="runs-card">
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.slate }}>Profile runs</h3>
            {!runs.length ? (
              <p className="text-[11px]" style={{ color: C.slate }} data-testid="runs-empty">
                <Database className="w-3 h-3 inline mr-1" />No profile has been run yet. Nothing is assumed about the book until one is.
              </p>
            ) : (
              <table className="w-full text-xs">
                <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
                  <th className="py-1 pr-2">When</th><th className="py-1 pr-2">Scope</th><th className="py-1 pr-2">Shapes</th>
                  <th className="py-1 pr-2">Rows</th><th className="py-1 pr-2">Readiness</th><th className="py-1 pr-2">Findings</th>
                  <th className="py-1 pr-2">Orphans</th><th className="py-1">By</th>
                </tr></thead>
                <tbody>
                  {runs.map(r => (
                    <tr key={r.run_id} onClick={() => openRun(r.run_id)} data-testid={`run-row-${r.run_id}`}
                      className="border-t cursor-pointer hover:bg-slate-50" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
                      <td className="py-1.5 pr-2" style={{ color: C.navy }}>{r.finished_at?.slice(0, 16).replace('T', ' ')}</td>
                      <td className="py-1.5 pr-2">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
                          style={{ background: (r.scope === 'extract' ? C.amber : C.blue) + '15', color: r.scope === 'extract' ? C.amber : C.blue }}>
                          {r.scope === 'extract' ? 'extract' : 'platform'}
                        </span>
                      </td>
                      <td className="py-1.5 pr-2 text-[10px]" style={{ color: C.slate }}>{(r.entities || []).join(', ')}</td>
                      <td className="py-1.5 pr-2 font-mono" style={{ color: C.navy }}>{n(r.totals?.rows)}</td>
                      <td className="py-1.5 pr-2 font-mono font-semibold" style={{ color: score(r.totals?.readiness || 0) }}>{r.totals?.readiness}</td>
                      <td className="py-1.5 pr-2 font-mono" style={{ color: r.totals?.high ? C.red : C.slate }}>
                        {n(r.totals?.issues)}{r.totals?.high ? ` · ${r.totals.high} high` : ''}
                      </td>
                      <td className="py-1.5 pr-2 font-mono" style={{ color: r.totals?.orphans ? C.red : C.slate }}>{n(r.totals?.orphans)}</td>
                      <td className="py-1.5 text-[10px]" style={{ color: C.slate }}>{r.run_by?.email || '—'}{r.client ? ` · ${r.client}` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
